import React, { useState, useRef, useEffect } from "react";
import { Button, Modal, Spinner } from "react-bootstrap";
import {
  FaChevronDown,
  FaChevronUp,
  FaCalendarAlt,
  FaEnvelope,
} from "react-icons/fa";
import "./EventCard.css";
import {
  convertToDate,
  convertToTime,
} from "../../../../backend/timeFunctions";
import EmailSendingModal from "../../Email Sending/EmailSendingModal";
import { generateEventInvitationBody } from "../../Email Sending/emailTemplates";
// Event CRUD operations are now handled by parent component with real-time listeners
import { Timestamp } from "firebase/firestore";

const getRelativeDaysLabel = (eventDate) => {
  if (!eventDate) return null;
  const now = new Date();
  const eventDay = new Date(
    eventDate.getFullYear(),
    eventDate.getMonth(),
    eventDate.getDate()
  );
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const diffMs = eventDay - today;
  const diffDays = Math.round(diffMs / (1000 * 60 * 60 * 24));

  if (diffDays === 0)
    return (
      <span style={{ color: "#888", fontWeight: 600, marginRight: 8 }}>
        היום
      </span>
    );
  if (diffDays === 1)
    return (
      <span style={{ color: "#888", fontWeight: 600, marginRight: 8 }}>
        עוד יום
      </span>
    );
  if (diffDays === 2)
    return (
      <span style={{ color: "#888", fontWeight: 600, marginRight: 8 }}>
        עוד יומיים
      </span>
    );
  if (diffDays > 2)
    return (
      <span style={{ color: "#888", fontWeight: 600, marginRight: 8 }}>
        עוד {diffDays} ימים
      </span>
    );
  if (diffDays === -1)
    return (
      <span style={{ color: "#888", fontWeight: 600, marginRight: 8 }}>
        לפני יום
      </span>
    );
  if (diffDays === -2)
    return (
      <span style={{ color: "#888", fontWeight: 600, marginRight: 8 }}>
        לפני יומיים
      </span>
    );
  if (diffDays < -2)
    return (
      <span style={{ color: "#888", fontWeight: 600, marginRight: 8 }}>
        לפני {Math.abs(diffDays)} ימים
      </span>
    );
  return null;
};

const EventCard = ({
  event,
  groups,
  students,
  onEdit,
  onDelete,
  isDeleting,
  userData,
  userID,
  hasEditPrivilege,
  onEventUpdate,
}) => {
  // Convert invitation sent timestamp to readable date
  const getInvitationSentDate = () => {
    if (!event?.invitationSentTimestamp) return null;
    const timestamp = event?.invitationSentTimestamp;
    const date = timestamp.seconds
      ? new Date(timestamp.seconds * 1000)
      : new Date(timestamp);
    return date.toLocaleDateString("he-IL");
  };

  // Handle successful email sending
  const handleEmailSent = async (emailData) => {
    try {
      const updatedEvent = {
        ...event,
        invitationSentTimestamp: Timestamp.now(),
      };
      // Use parent component handler which will update database and real-time listener will handle state
      if (onEventUpdate) {
        await onEventUpdate(updatedEvent);
      }
    } catch (error) {
      console.error("Error updating event with invitation timestamp:", error);
    }
  };
  const [expanded, setExpanded] = useState(false);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [showEmailModal, setShowEmailModal] = useState(false);
  const detailsRef = useRef(null);
  const [detailsHeight, setDetailsHeight] = useState(0);

  useEffect(() => {
    if (expanded && detailsRef.current) {
      setDetailsHeight(detailsRef.current.scrollHeight);
    } else {
      setDetailsHeight(0);
    }
  }, [expanded]);

  const handleDelete = () => {
    if (!hasEditPrivilege) return;
    setShowConfirmation(true);
  };

  const confirmDelete = () => {
    if (!hasEditPrivilege) return;
    onDelete(event);
    setShowConfirmation(false);
  };

  const cancelDelete = () => {
    setShowConfirmation(false);
  };

  const toggleExpanded = () => {
    setExpanded((expanded) => !expanded);
  };

  let formattedDate = event.date
    ? convertToDate(event.date.seconds, event.date.nanoseconds)
    : "לא נקבע";

  // For relative days label
  let eventDateObj = null;
  if (event.date && event.date.seconds) {
    eventDateObj = new Date(event.date.seconds * 1000);
  }
  const relativeDaysLabel = getRelativeDaysLabel(eventDateObj);

  // Get group names for display
  const getGroupNames = () => {
    if (!event.groupsInEvent || !groups) return [];

    return event.groupsInEvent
      .map((groupId) => {
        const group = groups.find((g) => g.id === groupId);
        return group ? `${group.name} - ${group.dojo}` : null;
      })
      .filter(Boolean);
  };

  // Get event recipients (students from participating groups)
  const getEventRecipients = () => {
    if (!event.groupsInEvent || !students || !groups) return [];
    const participatingStudents = students.filter((student) =>
      event.groupsInEvent.some(
        (groupId) => student.groupsIDs && student.groupsIDs.includes(groupId)
      )
    );
    const recipients = [];

    participatingStudents.forEach((student) => {
      // Add first parent email if available
      if (student.firstParentEmailAddress) {
        recipients.push({
          id: `${student.id}_parent1`,
          name: `${student.name}`,
          email: student.firstParentEmailAddress,
        });
      }

      // Add second parent email if available
      if (student.secondParentEmailAddress) {
        recipients.push({
          id: `${student.id}_parent2`,
          name: `${student.name}`,
          email: student.secondParentEmailAddress,
        });
      }
    });

    return recipients;
  };

  const groupNames = getGroupNames();

  // Accent color (blue for now, could be dynamic)
  const accentColor = "#1976d2";

  return (
    <>
      <style>{`
        .modern-event-card-outer {
          background: #fff;
          border-radius: 18px;
          box-shadow: 0 2px 12px rgba(0,0,0,0.06);
          margin-bottom: 28px;
          display: flex;
          flex-direction: column;
          border-right: 8px solid ${accentColor};
          transition: box-shadow 0.2s, transform 0.2s;
          cursor: pointer;
        }
        .modern-event-card-outer:hover {
          box-shadow: 0 4px 24px ${accentColor}33;
          transform: translateY(-2px) scale(1.01);
        }
        .modern-event-header {
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: 22px 28px 18px 28px;
          border-radius: 18px 18px 0 0;
          background: #f8fafc;
          transition: background 0.2s;
        }
        .modern-event-header:hover {
          background: #e3e9f7;
        }
        .modern-event-title {
          font-size: 1.35rem;
          font-weight: 800;
          color: #222;
          display: flex;
          align-items: center;
          gap: 12px;
        }
        .modern-event-date {
          font-size: 1.05rem;
          color: #1976d2;
          font-weight: 700;
          margin-right: 12px;
          display: flex;
          align-items: center;
          gap: 6px;
        }
        .modern-chevron {
          font-size: 1.2em;
          color: #888;
          margin-right: 12px;
          transition: transform 0.2s;
        }
        .modern-event-details-outer {
          overflow: hidden;
          transition: max-height 0.45s cubic-bezier(0.4,0.0,0.2,1), opacity 0.35s cubic-bezier(0.4,0.0,0.2,1);
          opacity: 1;
        }
        .modern-event-details-outer.closed {
          opacity: 0;
        }
        .modern-event-details {
          background: #f5f7fa;
          border-radius: 0 0 18px 18px;
          padding: 28px 32px 20px 32px;
          font-size: 1.08rem;
          color: #333;
          margin-top: -2px;
        }
        .modern-event-details strong {
          color: #1976d2;
          font-weight: 700;
        }
        .modern-group-list {
          margin: 0 0 0 0;
          padding: 0 0 0 24px;
        }
        .modern-event-actions {
          display: flex;
          gap: 16px;
          margin-top: 24px;
        }
        .modern-event-actions .btn {
          border-radius: 999px !important;
          font-weight: 700 !important;
          font-size: 1.05rem !important;
          min-width: 100px;
        }
        .modern-invite-btn {
          border-radius: 999px !important;
          font-weight: 700 !important;
          font-size: 1.05rem !important;
          min-width: 140px;
          margin-right: 8px;
          background: linear-gradient(135deg, #1976d2 0%, #42a5f5 100%) !important;
          border: none !important;
          color: white !important;
          transition: all 0.3s ease !important;
          display: flex !important;
          align-items: center !important;
          justify-content: center !important;
          gap: 8px !important;
        }
                 .modern-invite-btn:hover {
           background: linear-gradient(135deg, #1565c0 0%, #1976d2 100%) !important;
           transform: translateY(-2px) !important;
           box-shadow: 0 4px 12px rgba(25, 118, 210, 0.4) !important;
         }
         .invitation-sent-badge {
           background: linear-gradient(135deg, #4caf50 0%, #66bb6a 100%);
           color: white;
           padding: 4px 12px;
           border-radius: 20px;
           font-size: 0.85rem;
           font-weight: 600;
           margin-right: 12px;
           display: flex;
           align-items: center;
           gap: 6px;
           box-shadow: 0 2px 8px rgba(76, 175, 80, 0.3);
         }
         .invitation-sent-badge-expanded {
           background: linear-gradient(135deg, #4caf50 0%, #66bb6a 100%);
           color: white;
           padding: 6px 16px;
           border-radius: 25px;
           font-size: 0.9rem;
           font-weight: 600;
           margin-top: 16px;
           display: inline-flex;
           align-items: center;
           gap: 8px;
           box-shadow: 0 2px 8px rgba(76, 175, 80, 0.3);
         }
         .invitation-not-sent-badge {
           background: linear-gradient(135deg, #9e9e9e 0%, #bdbdbd 100%);
           color: white;
           padding: 4px 12px;
           border-radius: 20px;
           font-size: 0.85rem;
           font-weight: 600;
           margin-right: 12px;
           display: flex;
           align-items: center;
           gap: 6px;
           box-shadow: 0 2px 8px rgba(158, 158, 158, 0.3);
         }
         .invitation-not-sent-badge-expanded {
           background: linear-gradient(135deg, #9e9e9e 0%, #bdbdbd 100%);
           color: white;
           padding: 6px 16px;
           border-radius: 25px;
           font-size: 0.9rem;
           font-weight: 600;
           margin-top: 16px;
           display: inline-flex;
           align-items: center;
           gap: 8px;
           box-shadow: 0 2px 8px rgba(158, 158, 158, 0.3);
         }
      `}</style>
      <div className="modern-event-card-outer" onClick={toggleExpanded}>
        <div
          className="modern-event-header"
          onClick={(e) => {
            e.stopPropagation();
            toggleExpanded();
          }}
        >
          <div className="modern-event-title">
            {event.name}
            <span className="modern-event-date">
              <FaCalendarAlt style={{ marginLeft: 4 }} />
              {formattedDate}
              {relativeDaysLabel}
            </span>
            {event?.invitationSentTimestamp ? (
              <span className="invitation-sent-badge">
                <FaEnvelope />
                הזמנה נשלחה!
              </span>
            ) : (
              <span className="invitation-not-sent-badge">
                <FaEnvelope />
                הזמנה טרם נשלחה
              </span>
            )}
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <span className="modern-chevron">
              {expanded ? <FaChevronUp /> : <FaChevronDown />}
            </span>
          </div>
        </div>
        <div
          className={`modern-event-details-outer${expanded ? "" : " closed"}`}
          style={{
            maxHeight: expanded ? detailsHeight + 40 : 0,
            opacity: expanded ? 1 : 0,
          }}
        >
          <div
            className="modern-event-details"
            ref={detailsRef}
            aria-hidden={!expanded}
          >
            <p>
              <strong>מיקום:</strong> {event.location || "לא הוגדר"}
            </p>
            <p>
              <strong>זמן התחלה:</strong>{" "}
              {event.date
                ? convertToTime(event.date.seconds, event.date.nanoseconds)
                : "לא נקבע"}
            </p>
            <p>
              <strong>זמן סיום:</strong>{" "}
              {event.endDate
                ? convertToTime(
                  event.endDate.seconds,
                  event.endDate.nanoseconds
                )
                : "לא נקבע"}
            </p>
            <p>
              <strong>תיאור:</strong> {event.details || "ללא"}
            </p>
            {event?.paymentUrl && event?.paymentUrl !== "" && (
              <p>
                <strong>קישור תשלום לאירוע:</strong>{" "}
                <a
                  href={event.paymentUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  דף התשלום
                </a>
              </p>
            )}
            {groupNames.length > 0 && (
              <div style={{ direction: "rtl" }}>
                <p><strong>קבוצות משתתפות:</strong></p>
                <ul className="modern-group-list">
                  {groupNames.map((groupName, index) => (
                    <li key={index}>{groupName}</li>
                  ))}
                </ul>
              </div>
            )}
            {event?.invitationSentTimestamp ? (
              <div style={{ direction: "rtl", textAlign: "right" }}>
                <span className="invitation-sent-badge-expanded">
                  <FaEnvelope />
                  הזמנה נשלחה בתאריך {getInvitationSentDate()}
                </span>
              </div>
            ) : (
              <div style={{ direction: "rtl", textAlign: "right" }}>
                <span className="invitation-not-sent-badge-expanded">
                  <FaEnvelope />
                  הזמנה טרם נשלחה
                </span>
              </div>
            )}
            {hasEditPrivilege && (
              <div className="modern-event-actions">
                <Button variant="warning" onClick={() => onEdit(event)}>
                  עריכה
                </Button>
                <Button
                  variant="danger"
                  onClick={handleDelete}
                  disabled={isDeleting}
                >
                  {isDeleting ? (
                    <>
                      <Spinner
                        as="span"
                        animation="border"
                        size="sm"
                        role="status"
                        aria-hidden="true"
                        className="me-2"
                      />
                      מוחק...
                    </>
                  ) : (
                    "מחיקה"
                  )}
                </Button>
                <Button
                  variant="primary"
                  className="modern-invite-btn"
                  onClick={(e) => {
                    e.stopPropagation();
                    setShowEmailModal(true);
                  }}
                >
                  <FaEnvelope />
                  שלח הזמנה במייל
                </Button>
              </div>
            )}

            {hasEditPrivilege && (
              <Modal show={showConfirmation} onHide={cancelDelete}>
                <Modal.Header className="hebrewHeader">
                  <Modal.Title className="hebrewText">
                    אישור מחיקת אירוע
                  </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                  <p className="hebrewText">
                    האם אתה בטוח שברצונך למחוק את האירוע "{event.name}"?
                    <strong>שים לב, פעולה זו היא בלתי הפיכה!</strong>
                  </p>
                </Modal.Body>
                <Modal.Footer>
                  <Button
                    variant="danger"
                    onClick={confirmDelete}
                    disabled={isDeleting}
                  >
                    {isDeleting ? (
                      <>
                        <Spinner
                          as="span"
                          animation="border"
                          size="sm"
                          role="status"
                          aria-hidden="true"
                          className="me-2"
                        />
                        מוחק...
                      </>
                    ) : (
                      "הבנתי, מחק בכל זאת"
                    )}
                  </Button>
                  <Button variant="secondary" onClick={cancelDelete}>
                    ביטול
                  </Button>
                </Modal.Footer>
              </Modal>
            )}
          </div>
        </div>
      </div>
      <EmailSendingModal
        isOpen={showEmailModal}
        onClose={() => {
          setShowEmailModal(false);
        }}
        title="שליחת הזמנה לאירוע"
        recipients={getEventRecipients()}
        allowRecipientSelection={true}
        defaultSubject={`הזמנה לאירוע: ${event.name}`}
        emailBody={generateEventInvitationBody(event, {
          includePaymentButton: true,
          customMessage: "",
          userOrganizationName: userData?.userOrganizationName || "SimplyCoach",
        })}
        event={event}
        onSuccessSend={handleEmailSent}
      />
    </>
  );
};

export default EventCard;
